import { useState, useEffect } from 'react';
import {
  Settings2,
  ToggleLeft,
  Type,
  Check,
  RotateCcw,
  Shield,
  LayoutDashboard,
  PanelLeftClose,
  Sparkles,
  PanelRight,
  Palette,
  ExternalLink,
  Users,
  MapPin,
  Monitor,
  DollarSign,
  CreditCard,
  Boxes,
  SidebarOpen,
  LayoutGrid,
  Globe,
  HardDrive,
  RefreshCw,
  Database,
  Trash2,
  Loader2,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFeatureFlags, SYSTEM_FONTS, SystemFont } from '@/hooks/useFeatureFlags';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { Navigate } from 'react-router-dom';
import { Timer } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useQueryClient } from '@tanstack/react-query';
import { ModuleSettingsSection } from '@/components/settings/ModuleSettingsSection';

export default function Utilities() {
  const { profile, loading, organization, refetchProfile } = useAuth();
  const { flags, setFlag, systemFont, setSystemFont, resetFlags } = useFeatureFlags();
  const queryClient = useQueryClient();
  const [showSaved, setShowSaved] = useState(false);
  const [orgUseAlternativeLogin, setOrgUseAlternativeLogin] = useState(false);
  const [savingOrgSetting, setSavingOrgSetting] = useState(false);
  const [clearingCache, setClearingCache] = useState<'data' | 'all' | null>(null);
  const [savingPreset, setSavingPreset] = useState(false);

  // Current organization theme preset (Blaze vs Lightspeed). Defaults to 'blaze'.
  const themePreset =
    ((organization?.settings as { appearance?: { preset?: string } } | null | undefined)
      ?.appearance?.preset) === 'lightspeed'
      ? 'lightspeed'
      : 'blaze';

  // Fetch organization-level alternative login setting
  useEffect(() => {
    if (organization?.settings) {
      const settings = organization.settings as { use_alternative_login?: boolean };
      setOrgUseAlternativeLogin(settings.use_alternative_login ?? false);
    }
  }, [organization?.settings]);

  // Save alternative login setting to organization (global for all users)
  const handleAlternativeLoginToggle = async (checked: boolean) => {
    if (!organization?.id) {
      console.error('[Utilities] No organization ID available');
      return;
    }

    setSavingOrgSetting(true);
    try {
      const currentSettings = organization.settings || {};
      const newSettings = {
        ...currentSettings,
        use_alternative_login: checked,
      };

      const { error } = await supabase
        .from('organizations')
        .update({
          settings: newSettings,
          updated_at: new Date().toISOString(),
        })
        .eq('id', organization.id)
        .select('id, slug, settings')
        .single();

      if (error) throw error;

      setOrgUseAlternativeLogin(checked);
      await refetchProfile(); // Refresh org settings
      showSavedIndicator();
    } catch (error) {
      console.error('[Utilities] Failed to update alternative login setting:', error);
    } finally {
      setSavingOrgSetting(false);
    }
  };

  // Change the organization theme preset via the superadmin-only RPC. The
  // ThemePresetProvider reacts to the refreshed organization settings.
  const handlePresetChange = async (preset: 'blaze' | 'lightspeed') => {
    if (!organization?.id || preset === themePreset) return;

    setSavingPreset(true);
    try {
      const { error } = await supabase.rpc('update_org_theme_preset', {
        p_org_id: organization.id,
        p_preset: preset,
      });
      if (error) throw error;

      await refetchProfile(); // Refresh org settings so the layout swaps
      showSavedIndicator();
    } catch (error) {
      console.error('[Utilities] Failed to update theme preset:', error);
    } finally {
      setSavingPreset(false);
    }
  };

  // Only superadmin can access this page
  if (!loading && profile?.role !== 'superadmin') {
    return <Navigate to="/dashboard" replace />;
  }

  const handleToggle = (key: keyof typeof flags, value: boolean) => {
    setFlag(key, value);
    showSavedIndicator();
  };

  const handleFontChange = (font: SystemFont) => {
    setSystemFont(font);
    showSavedIndicator();
  };

  const handleReset = () => {
    resetFlags();
    setSystemFont('inter');
    showSavedIndicator();
  };

  const showSavedIndicator = () => {
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 2000);
  };

  // Cache clearing functions
  const clearDataCache = () => {
    setClearingCache('data');
    queryClient.clear();
    setTimeout(() => {
      setClearingCache(null);
      showSavedIndicator();
    }, 500);
  };

  const hardRefresh = () => {
    // Add cache-busting timestamp to force fresh load
    window.location.href = window.location.pathname + '?_=' + Date.now();
  };

  const clearAllAndReload = async () => {
    setClearingCache('all');
    
    // Clear React Query cache
    queryClient.clear();
    
    // Preserve auth tokens
    const authKeys = [
      'sb-auth-token',
      'supabase.auth.token',
      // Supabase stores auth with dynamic project ref prefix
    ];
    const savedAuth: Record<string, string | null> = {};
    
    // Save all supabase auth keys (they have dynamic prefixes)
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('supabase.auth') || key.startsWith('sb-'))) {
        savedAuth[key] = localStorage.getItem(key);
      }
    }
    authKeys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value) savedAuth[key] = value;
    });
    
    // Clear storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Restore auth tokens
    Object.entries(savedAuth).forEach(([key, value]) => {
      if (value) localStorage.setItem(key, value);
    });
    
    // Clear CacheStorage if available (for PWA/service workers)
    if ('caches' in window) {
      try {
        const keys = await caches.keys();
        await Promise.all(keys.map(key => caches.delete(key)));
      } catch (e) {
        console.warn('Failed to clear CacheStorage:', e);
      }
    }
    
    // Hard refresh after a short delay
    setTimeout(() => {
      hardRefresh();
    }, 300);
  };

  const header = (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-500/10">
          <Settings2 className="h-5 w-5 text-purple-600" />
        </div>
        <div>
          <h1 className="text-2xl font-display font-semibold tracking-tight">
            Utilities
          </h1>
          <p className="text-sm text-muted-foreground">
            Superadmin feature flags and system settings
          </p>
        </div>
      </div>
      {showSaved && (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-500/10 text-green-600 text-sm font-medium animate-in fade-in">
          <Check className="h-4 w-4" />
          Saved
        </div>
      )}
    </div>
  );

  return (
    <DashboardLayout header={header}>
      <Tabs defaultValue="general" className="space-y-6">
        <TabsList>
          <TabsTrigger value="general">General Utilities</TabsTrigger>
          <TabsTrigger value="modules">Modules</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-0">
          <div className="space-y-6">
        {/* Superadmin Notice */}
        <div className="flex items-center gap-3 rounded-xl border border-purple-500/20 bg-purple-500/5 px-4 py-3">
          <Shield className="h-5 w-5 text-purple-600" />
          <div>
            <p className="text-sm font-medium text-purple-600">Superadmin Access</p>
            <p className="text-xs text-muted-foreground">
              These settings are only visible to superadmins and affect the entire system.
            </p>
          </div>
        </div>

        {/* Admin Console Access */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Admin Console</CardTitle>
              </div>
              <a
                href={import.meta.env.VITE_ADMIN_URL || 'https://phoenix.blazepos.app'}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="blaze" className="gap-2">
                  <ExternalLink className="h-4 w-4" />
                  Open Admin Console
                </Button>
              </a>
            </div>
            <CardDescription>
              Access the BlazePOS admin dashboard to manage all clients, billing, and subscriptions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 rounded-lg bg-blue-500/10 text-center">
                <Users className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                <p className="text-2xl font-semibold text-blue-600">—</p>
                <p className="text-xs text-muted-foreground">Total Clients</p>
              </div>
              <div className="p-4 rounded-lg bg-purple-500/10 text-center">
                <MapPin className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                <p className="text-2xl font-semibold text-purple-600">—</p>
                <p className="text-xs text-muted-foreground">Locations</p>
              </div>
              <div className="p-4 rounded-lg bg-orange-500/10 text-center">
                <Monitor className="h-6 w-6 text-orange-600 mx-auto mb-2" />
                <p className="text-2xl font-semibold text-orange-600">—</p>
                <p className="text-xs text-muted-foreground">Registers</p>
              </div>
              <div className="p-4 rounded-lg bg-green-500/10 text-center">
                <DollarSign className="h-6 w-6 text-green-600 mx-auto mb-2" />
                <p className="text-2xl font-semibold text-green-600">—</p>
                <p className="text-xs text-muted-foreground">Total MRR</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-4 text-center">
              Stats shown here require connection to the admin API. Open the Admin Console for full details.
            </p>
          </CardContent>
        </Card>

        {/* Appearance / Theme Preset */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Palette className="h-5 w-5 text-muted-foreground" />
              <CardTitle>Appearance</CardTitle>
            </div>
            <CardDescription>
              Choose the layout theme for {organization?.name || 'this organization'}. Applies to
              everyone in the organization.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2">
              {([
                {
                  value: 'blaze' as const,
                  title: 'BlazePOS',
                  desc: 'Vertical sidebar, orange accent, rounded surfaces.',
                },
                {
                  value: 'lightspeed' as const,
                  title: 'Lightspeed',
                  desc: 'Left icon rail with contextual sub-nav, teal accent, denser layout.',
                },
              ]).map((option) => {
                const selected = themePreset === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    disabled={savingPreset}
                    onClick={() => handlePresetChange(option.value)}
                    className={cn(
                      'flex flex-col items-start gap-1 rounded-xl border p-4 text-left transition-colors',
                      selected
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'border-border hover:bg-muted/50',
                      savingPreset && 'opacity-60 cursor-not-allowed'
                    )}
                  >
                    <div className="flex w-full items-center justify-between gap-2">
                      <span className="font-medium">{option.title}</span>
                      {selected && (
                        <Badge variant="secondary" className="gap-1">
                          <Check className="h-3 w-3" />
                          Active
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">{option.desc}</span>
                  </button>
                );
              })}
            </div>
            {savingPreset && (
              <p className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                Applying theme...
              </p>
            )}
          </CardContent>
        </Card>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Feature Flags */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <ToggleLeft className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Feature Flags</CardTitle>
              </div>
              <CardDescription>
                Toggle experimental features and UI components
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Scrollable container - max 5 items visible */}
              <div className="max-h-[420px] overflow-y-auto space-y-4 pr-2">
                {/* Show Classic Dashboard Cards */}
                <div className="flex items-center justify-between p-4 rounded-xl border bg-card">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-500/10">
                      <LayoutDashboard className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <Label htmlFor="classic-cards" className="font-medium cursor-pointer">
                        Show Classic Dashboard Cards
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Display the 4 metric cards on the dashboard
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {flags.showClassicDashboardCards && (
                      <Badge variant="secondary" className="text-xs">On</Badge>
                    )}
                    <Switch
                      id="classic-cards"
                      checked={flags.showClassicDashboardCards}
                      onCheckedChange={(checked) => handleToggle('showClassicDashboardCards', checked)}
                    />
                  </div>
                </div>

                {/* Show Global View Notice */}
                <div className="flex items-center justify-between p-4 rounded-xl border bg-card">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-500/10">
                      <LayoutDashboard className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <Label htmlFor="global-view-notice" className="font-medium cursor-pointer">
                        Show Global View Notice
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Display "Viewing aggregated data across X locations" banner
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {flags.showGlobalViewNotice && (
                      <Badge variant="secondary" className="text-xs">On</Badge>
                    )}
                    <Switch
                      id="global-view-notice"
                      checked={flags.showGlobalViewNotice}
                      onCheckedChange={(checked) => handleToggle('showGlobalViewNotice', checked)}
                    />
                  </div>
                </div>

                {/* Auto-collapse Sidebar on Reports */}
                <div className="flex items-center justify-between p-4 rounded-xl border bg-card">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-orange-500/10">
                      <PanelLeftClose className="h-4 w-4 text-orange-600" />
                    </div>
                    <div>
                      <Label htmlFor="auto-collapse-sidebar" className="font-medium cursor-pointer">
                        Auto-collapse Sidebar on Reports
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Automatically collapse the primary sidebar when viewing Reports
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {flags.autoCollapseSidebarOnReports && (
                      <Badge variant="secondary" className="text-xs">On</Badge>
                    )}
                    <Switch
                      id="auto-collapse-sidebar"
                      checked={flags.autoCollapseSidebarOnReports}
                      onCheckedChange={(checked) => handleToggle('autoCollapseSidebarOnReports', checked)}
                    />
                  </div>
                </div>

                {/* Auto-collapse Sidebar on Inventory */}
                <div className="flex items-center justify-between p-4 rounded-xl border bg-card">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-500/10">
                      <Boxes className="h-4 w-4 text-teal-600" />
                    </div>
                    <div>
                      <Label htmlFor="auto-collapse-sidebar-inventory" className="font-medium cursor-pointer">
                        Auto-collapse Sidebar on Inventory
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Automatically collapse the primary sidebar when viewing Inventory
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {flags.autoCollapseSidebarOnInventory && (
                      <Badge variant="secondary" className="text-xs">On</Badge>
                    )}
                    <Switch
                      id="auto-collapse-sidebar-inventory"
                      checked={flags.autoCollapseSidebarOnInventory}
                      onCheckedChange={(checked) => handleToggle('autoCollapseSidebarOnInventory', checked)}
                    />
                  </div>
                </div>

                {/* Auto-clear POS Order */}
                <div className="flex items-center justify-between p-4 rounded-xl border bg-card">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-500/10">
                      <Timer className="h-4 w-4 text-red-600" />
                    </div>
                    <div>
                      <Label htmlFor="auto-clear-order" className="font-medium cursor-pointer">
                        Auto-clear POS Order on Leave
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Clear in-progress POS orders when navigating away. Default off to preserve carts.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {flags.autoClearPosOrderOnLeave && (
                      <Badge variant="secondary" className="text-xs">On</Badge>
                    )}
                    <Switch
                      id="auto-clear-order"
                      checked={flags.autoClearPosOrderOnLeave}
                      onCheckedChange={(checked) => handleToggle('autoClearPosOrderOnLeave', checked)}
                    />
                  </div>
                </div>

                {/* Expanded Upsell Popover */}
                <div className="flex items-center justify-between p-4 rounded-xl border bg-card">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500/10">
                      <Sparkles className="h-4 w-4 text-amber-600" />
                    </div>
                    <div>
                      <Label htmlFor="expanded-upsell" className="font-medium cursor-pointer">
                        Expanded Upsell Suggestions
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Show larger upsell popover on POS. Default is compact mode for less distraction.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {flags.expandedUpsellPopover && (
                      <Badge variant="secondary" className="text-xs">On</Badge>
                    )}
                    <Switch
                      id="expanded-upsell"
                      checked={flags.expandedUpsellPopover}
                      onCheckedChange={(checked) => handleToggle('expandedUpsellPopover', checked)}
                    />
                  </div>
                </div>

                {/* Full Panel Product View */}
                <div className="flex items-center justify-between p-4 rounded-xl border bg-card">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-purple-500/10">
                      <PanelRight className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <Label htmlFor="product-view-panel" className="font-medium cursor-pointer">
                        Full Panel Product View
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Open products in a slide-out panel instead of inline row expansion.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {flags.productViewFullPanel && (
                      <Badge variant="secondary" className="text-xs">On</Badge>
                    )}
                    <Switch
                      id="product-view-panel"
                      checked={flags.productViewFullPanel}
                      onCheckedChange={(checked) => handleToggle('productViewFullPanel', checked)}
                    />
                  </div>
                </div>

                {/* Vibrant Avatar Colors */}
                <div className="flex items-center justify-between p-4 rounded-xl border bg-card">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-pink-500/10">
                      <Palette className="h-4 w-4 text-pink-600" />
                    </div>
                    <div>
                      <Label htmlFor="vibrant-avatars" className="font-medium cursor-pointer">
                        Vibrant Avatar Colors
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Use unique per-user colors with white text instead of role-based pastel colors.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {flags.vibrantAvatars && (
                      <Badge variant="secondary" className="text-xs">On</Badge>
                    )}
                    <Switch
                      id="vibrant-avatars"
                      checked={flags.vibrantAvatars}
                      onCheckedChange={(checked) => handleToggle('vibrantAvatars', checked)}
                    />
                  </div>
                </div>

                {/* Show Team Settings */}
                <div className="flex items-center justify-between p-4 rounded-xl border bg-card">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-500/10">
                      <Users className="h-4 w-4 text-indigo-600" />
                    </div>
                    <div>
                      <Label htmlFor="show-team-settings" className="font-medium cursor-pointer">
                        Show Team Settings
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Enable the Team management section in Settings (normally in Command Center)
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {flags.showTeamSettings && (
                      <Badge variant="secondary" className="text-xs">On</Badge>
                    )}
                    <Switch
                      id="show-team-settings"
                      checked={flags.showTeamSettings}
                      onCheckedChange={(checked) => handleToggle('showTeamSettings', checked)}
                    />
                  </div>
                </div>

                {/* Show Location Settings */}
                <div className="flex items-center justify-between p-4 rounded-xl border bg-card">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-cyan-500/10">
                      <MapPin className="h-4 w-4 text-cyan-600" />
                    </div>
                    <div>
                      <Label htmlFor="show-location-settings" className="font-medium cursor-pointer">
                        Show Location Settings
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Enable the Locations management section in Settings (normally in Command Center)
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {flags.showLocationSettings && (
                      <Badge variant="secondary" className="text-xs">On</Badge>
                    )}
                    <Switch
                      id="show-location-settings"
                      checked={flags.showLocationSettings}
                      onCheckedChange={(checked) => handleToggle('showLocationSettings', checked)}
                    />
                  </div>
                </div>

                {/* Show Payment Settings */}
                <div className="flex items-center justify-between p-4 rounded-xl border bg-card">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/10">
                      <CreditCard className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div>
                      <Label htmlFor="show-payment-settings" className="font-medium cursor-pointer">
                        Show Payment Settings
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Enable the Payments management section in Settings (normally in Command Center)
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {flags.showPaymentSettings && (
                      <Badge variant="secondary" className="text-xs">On</Badge>
                    )}
                    <Switch
                      id="show-payment-settings"
                      checked={flags.showPaymentSettings}
                      onCheckedChange={(checked) => handleToggle('showPaymentSettings', checked)}
                    />
                  </div>
                </div>

                {/* Show Sidebar Section Headers */}
                <div className="flex items-center justify-between p-4 rounded-xl border bg-card">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-500/10">
                      <SidebarOpen className="h-4 w-4 text-slate-600" />
                    </div>
                    <div>
                      <Label htmlFor="show-sidebar-section-headers" className="font-medium cursor-pointer">
                        Show Sidebar Section Headers
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Display section headers (Operations, Kitchen, etc.) in the sidebar navigation
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {flags.showSidebarSectionHeaders && (
                      <Badge variant="secondary" className="text-xs">On</Badge>
                    )}
                    <Switch
                      id="show-sidebar-section-headers"
                      checked={flags.showSidebarSectionHeaders}
                      onCheckedChange={(checked) => handleToggle('showSidebarSectionHeaders', checked)}
                    />
                  </div>
                </div>

                {/* Show Floor Plan */}
                <div className="flex items-center justify-between p-4 rounded-xl border bg-card">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-500/10">
                      <LayoutGrid className="h-4 w-4 text-violet-600" />
                    </div>
                    <div>
                      <Label htmlFor="show-floor-plan" className="font-medium cursor-pointer">
                        Show Floor Plan
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Enable Floor Plan feature in the sidebar for table management
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {flags.showFloorPlan && (
                      <Badge variant="secondary" className="text-xs">On</Badge>
                    )}
                    <Switch
                      id="show-floor-plan"
                      checked={flags.showFloorPlan}
                      onCheckedChange={(checked) => handleToggle('showFloorPlan', checked)}
                    />
                  </div>
                </div>

                {/* Alternative Login Screen - Organization-wide setting */}
                <div className="flex items-center justify-between p-4 rounded-xl border bg-card">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-500/10">
                      <Globe className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <Label htmlFor="alternative-login" className="font-medium cursor-pointer">
                        Alternative Login Screen
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Use modern split-screen login design for all users (organization-wide)
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {orgUseAlternativeLogin && (
                      <Badge variant="secondary" className="text-xs">Global</Badge>
                    )}
                    <Switch
                      id="alternative-login"
                      checked={orgUseAlternativeLogin}
                      onCheckedChange={handleAlternativeLoginToggle}
                      disabled={savingOrgSetting}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* System Font Picker */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Type className="h-5 w-5 text-muted-foreground" />
                <CardTitle>System Font</CardTitle>
              </div>
              <CardDescription>
                Choose the font family used throughout the application
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="font-select">Font Family</Label>
                <Select value={systemFont} onValueChange={(value) => handleFontChange(value as SystemFont)}>
                  <SelectTrigger id="font-select" className="w-full">
                    <SelectValue placeholder="Select a font" />
                  </SelectTrigger>
                  <SelectContent>
                    {SYSTEM_FONTS.map((font) => (
                      <SelectItem key={font.value} value={font.value}>
                        <span style={{ fontFamily: font.fontFamily }}>{font.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Font Preview */}
              <div className="p-4 rounded-xl border bg-muted/30">
                <p className="text-xs text-muted-foreground mb-2">Preview</p>
                <p 
                  className="text-lg font-medium"
                  style={{ fontFamily: SYSTEM_FONTS.find(f => f.value === systemFont)?.fontFamily }}
                >
                  The quick brown fox jumps over the lazy dog.
                </p>
                <p 
                  className="text-sm mt-1"
                  style={{ fontFamily: SYSTEM_FONTS.find(f => f.value === systemFont)?.fontFamily }}
                >
                  0123456789 · $1,234.56 · BlazePOS
                </p>
              </div>

              <p className="text-xs text-muted-foreground">
                Font changes are applied immediately and saved to your browser.
              </p>
            </CardContent>
          </Card>

          {/* Cache & Storage */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <HardDrive className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Cache & Storage</CardTitle>
              </div>
              <CardDescription>
                Clear cached data to resolve display issues after updates
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Version Info */}
              <div className="p-4 rounded-xl border bg-muted/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">App Version</p>
                    <p className="font-mono text-sm font-medium">
                      {import.meta.env.VITE_APP_VERSION || 'dev'}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {import.meta.env.MODE}
                  </Badge>
                </div>
              </div>

              {/* Cache Actions */}
              <div className="space-y-3">
                {/* Clear Data Cache */}
                <div className="flex items-center justify-between p-4 rounded-xl border bg-card">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-500/10">
                      <Database className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">Clear Data Cache</p>
                      <p className="text-xs text-muted-foreground">
                        Refresh all data from the server
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearDataCache}
                    disabled={clearingCache !== null}
                  >
                    {clearingCache === 'data' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                  </Button>
                </div>

                {/* Hard Refresh */}
                <div className="flex items-center justify-between p-4 rounded-xl border bg-card">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500/10">
                      <RefreshCw className="h-4 w-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">Hard Refresh</p>
                      <p className="text-xs text-muted-foreground">
                        Reload page bypassing browser cache
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={hardRefresh}
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </div>

                {/* Clear All & Reload */}
                <div className="flex items-center justify-between p-4 rounded-xl border border-red-500/20 bg-red-500/5">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-500/10">
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">Clear All & Reload</p>
                      <p className="text-xs text-muted-foreground">
                        Clear all caches and storage, then reload
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={clearAllAndReload}
                    disabled={clearingCache !== null}
                  >
                    {clearingCache === 'all' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Clear All'
                    )}
                  </Button>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Use these options if you're seeing outdated UI or data after an update.
                Your login session will be preserved.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Reset Section */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Reset All Settings</h3>
                <p className="text-sm text-muted-foreground">
                  Restore all utilities settings to their default values
                </p>
              </div>
              <Button variant="outline" className="gap-2" onClick={handleReset}>
                <RotateCcw className="h-4 w-4" />
                Reset to Defaults
              </Button>
            </div>
          </CardContent>
        </Card>
          </div>
        </TabsContent>

        <TabsContent value="modules" className="mt-0">
          <ModuleSettingsSection />
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}
